// components/TotalExpensesCard/TotalExpensesCard.jsx
import React, {useEffect, useState} from 'react';
import {Alert, Card, Statistic, Tooltip} from 'antd';
import {EuroOutlined, InfoCircleOutlined} from '@ant-design/icons';
import style from './styles/TotalExpensesCard.module.css';
import {instance} from "../../../../../API/API";
import ExpensesType from "./ExpensesType";
import MiddleStatistic from "./MiddleStatistic";
import dayjs from "dayjs";


const TotalExpensesCard = ({dateRange, selectedCategory, refreshKey}) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        total_expenses: 0,
        total_cash: 0,
        total_non_cash: 0,
        avg_monthly: 0,
        period_months: 0,
        components: {
            salary: 0,
            one_time: 0,
            periodic: 0
        }
    });

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                date_from: dateRange[0].format('YYYY-MM-DD'),
                date_to: dayjs(dateRange[1].format('YYYY-MM-DD'),'YYYY-MM-DD').endOf('month').format('YYYY-MM-DD'),
            };

            if (selectedCategory) {
                params.category_id = selectedCategory;
            }

            const response = await instance.get(`finance_04122025/get_total_expenses.php`, {
                params,
                withCredentials: true,
            });

            if (response.data.success) {
                // Если эндпоинт не возвращает cash/non_cash, нужно обновить его
                // Пока используем рассчетные значения
                const responseData = response.data.data;
                // Предположим, что эндпоинт теперь возвращает cash и non_cash
                setData({
                    total_expenses: responseData.total_expenses || 0,
                    total_cash: responseData.total_cash || 0,
                    total_non_cash: responseData.total_non_cash || 0,
                    avg_monthly: responseData.avg_monthly || 0,
                    period_months: responseData.period_months || 0,
                    components: responseData.components || {
                        salary: 0,
                        one_time: 0,
                        periodic: 0
                    }
                });
            } else {
                throw new Error(response.data.error || 'Failed to load total expenses');
            }
        } catch (err) {
            console.error('Error fetching total expenses:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dateRange, selectedCategory, refreshKey]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    };

    if (error) {
        return (
            <Card className={style.card}>
                <Alert
                    message="Ошибка"
                    description="Не удалось загрузить общие расходы"
                    type="error"
                    showIcon
                />
            </Card>
        );
    }

    return (
        <Card
            className={`${style.card} ${style.totalExpensesCard}`}
            hoverable
            loading={loading}
        >
            <div className={style.cardHeader}>
                <div className={style.title}>
                    <EuroOutlined className={style.icon}/>
                    <span>Общие расходы</span>
                </div>
                <Tooltip title="Сумма всех расходов за выбранный период">
                    <InfoCircleOutlined className={style.infoIcon}/>
                </Tooltip>
            </div>

            <div className={style.mainContent}>
                <Statistic
                    value={data.total_expenses}
                    precision={2}
                    suffix="€"
                    valueStyle={{
                        color: '#ff4d4f',
                        fontSize: '24px',
                        fontWeight: 600
                    }}
                    className={style.statistic}
                />
                <ExpensesType cash={formatCurrency(data.total_cash)} non_cash={formatCurrency(data.total_non_cash)}/>
                <MiddleStatistic summ={formatCurrency(data.avg_monthly)} period={data.period_months}/>

                <div className={style.cardFooter}>
                    <div className={style.breakdown}>
                        <div className={style.breakdownList}>
                            <div className={style.breakdownItem}>
                                <span className={`${style.breakdownDot} ${style.salaryDot}`}></span>
                                <span className={style.breakdownLabel}>Зарплата:</span>
                                <span className={style.breakdownValue}>
                                {formatCurrency(data.components.salary)}
                            </span>
                            </div>
                            <div className={style.breakdownItem}>
                                <span className={`${style.breakdownDot} ${style.periodicDot}`}></span>
                                <span className={style.breakdownLabel}>Периодические:</span>
                                <span className={style.breakdownValue}>
                                {formatCurrency(data.components.periodic)}
                            </span>
                            </div>
                            <div className={style.breakdownItem}>
                                <span className={`${style.breakdownDot} ${style.onetimeDot}`}></span>
                                <span className={style.breakdownLabel}>Разовые:</span>
                                <span className={style.breakdownValue}>
                                {formatCurrency(data.components.one_time)}
                            </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default TotalExpensesCard;